import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  applyGoogleTranslateLanguageWhenReady,
  GOOGLE_TRANSLATE_LANGUAGES,
  initializeGoogleTranslate,
} from "../googleTranslate";

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (language: string) => void;
  availableLanguages: { code: string; name: string; nativeName: string }[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const savedPreferredLanguage =
    typeof window !== "undefined" ? localStorage.getItem("preferredLanguage") : null;
  const initialLanguage = (savedPreferredLanguage || "en").split("-")[0];
  const [currentLanguage, setCurrentLanguage] = useState(
    GOOGLE_TRANSLATE_LANGUAGES.includes(initialLanguage) ? initialLanguage : "en"
  );

  const availableLanguages = [
    { code: "en", name: "English", nativeName: "English" },
    { code: "pt", name: "Portuguese", nativeName: "Português" },
    { code: "fr", name: "French", nativeName: "Français" },
    { code: "de", name: "German", nativeName: "Deutsch" },
  ];

  const changeLanguage = (language: string) => {
    const normalizedLanguage = GOOGLE_TRANSLATE_LANGUAGES.includes(language) ? language : "en";
    i18n.changeLanguage("en");
    setCurrentLanguage(normalizedLanguage);
    localStorage.setItem("i18nextLng", "en");
    localStorage.setItem("preferredLanguage", normalizedLanguage);
    applyGoogleTranslateLanguageWhenReady(normalizedLanguage);
  };

  useEffect(() => {
    initializeGoogleTranslate();
    const savedLanguage =
      localStorage.getItem("preferredLanguage") || localStorage.getItem("i18nextLng");
    if (savedLanguage && savedLanguage !== currentLanguage) {
      const normalizedLanguage = GOOGLE_TRANSLATE_LANGUAGES.includes(savedLanguage)
        ? savedLanguage
        : "en";
      i18n.changeLanguage("en");
      setCurrentLanguage(normalizedLanguage);
      localStorage.setItem("i18nextLng", "en");
      localStorage.setItem("preferredLanguage", normalizedLanguage);
      return applyGoogleTranslateLanguageWhenReady(normalizedLanguage);
    }
    i18n.changeLanguage("en");
    localStorage.setItem("i18nextLng", "en");
    return applyGoogleTranslateLanguageWhenReady(currentLanguage);
  }, []);

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    availableLanguages,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
